import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { CSSProperties } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';

import type { MatchesPlayedStat_so5LeaderboardGroupInterface } from './__generated__/index.graphql';

const ProgressWrapper = styled.div`
  background: var(--c-nd-150);
  border-radius: calc(var(--height) / 2);
  height: var(--height);
  overflow: hidden;
`;

const InnerProgress = styled.div`
  height: 100%;
`;

type Props = {
  so5LeaderboardGroup: Nullable<MatchesPlayedStat_so5LeaderboardGroupInterface>;
};

const PlayedProgressBar = ({
  so5LeaderboardGroup,
  height = 2,
}: {
  so5LeaderboardGroup: MatchesPlayedStat_so5LeaderboardGroupInterface;
  height?: number;
}) => {
  return (
    <ProgressWrapper
      style={
        {
          '--height': `${height}px`,
        } as CSSProperties
      }
    >
      <InnerProgress
        style={{
          width: `${so5LeaderboardGroup.percentageOfGamesPlayed * 100}%`,
          background: so5LeaderboardGroup.primaryColor,
        }}
      />
    </ProgressWrapper>
  );
};

export const MatchesPlayedStat = ({ so5LeaderboardGroup }: Props) => {
  if (!so5LeaderboardGroup) {
    return null;
  }
  return (
    <Tooltip
      title={
        <Vertical>
          <LabelL bold>
            <FormattedMessage
              id="MatchesPlayedStat.tooltipTitle2"
              defaultMessage="{percentage, number, percent} of games played"
              values={{
                percentage: so5LeaderboardGroup.percentageOfGamesPlayed,
              }}
            />
          </LabelL>
          <PlayedProgressBar
            so5LeaderboardGroup={so5LeaderboardGroup}
            height={4}
          />
        </Vertical>
      }
    >
      <Vertical gap={0}>
        <LabelS bold>
          <FormattedNumber
            value={so5LeaderboardGroup.percentageOfGamesPlayed}
            style="percent"
          />
        </LabelS>
        <PlayedProgressBar so5LeaderboardGroup={so5LeaderboardGroup} />
      </Vertical>
    </Tooltip>
  );
};

MatchesPlayedStat.fragments = {
  so5LeaderboardGroupInterface: gql`
    fragment MatchesPlayedStat_so5LeaderboardGroupInterface on So5LeaderboardGroupInterface {
      slug
      percentageOfGamesPlayed
      primaryColor: color(variant: PRIMARY)
    }
  ` as TypedDocumentNode<MatchesPlayedStat_so5LeaderboardGroupInterface>,
};
